import { FeatureId } from '@upright/common/iam/access/feature-access/types';
import { productKeyByFeatureId } from '@upright/common/iam/entitlement/licence';
import { findRequestCatalogueEntry } from '@upright/common/iam/entitlement/request-catalogue';
import { CustomerAccessRequest, EntitlementRequestState } from '@upright/common/iam/entitlement/request-api-types';
import { ResourcelessPermissionId } from '@upright/common/iam/privilege/permission/types';
import { getOrganizationOfUser } from '@upright/common/iam/organizations/common';
import { useHasResourcelessPrivilege } from '@upright/ui/api/iam/privilege/query-options';
import { useGetLoggedInUser, useIsTeam } from '@upright/ui/common/generic/hooks';

/** What a request asks for: a key of the request catalogue, or a company by its id. */
export type RequestTarget =
  | { key: string }
  | { companyId: string }
  | { key: string; companyId: string; scenarioId: string };

export const requestStateLabels: Readonly<Record<EntitlementRequestState, string>> = {
  [EntitlementRequestState.REQUESTED]: 'Requested',
  [EntitlementRequestState.WITHDRAWN]: 'Withdrawn',
  [EntitlementRequestState.DECLINED]: 'Declined',
  [EntitlementRequestState.GRANTED]: 'Granted',
  [EntitlementRequestState.PREPARING]: 'Preparing',
  [EntitlementRequestState.READY]: 'Ready',
  [EntitlementRequestState.ENDED]: 'Ended',
};

// Asked for and not yet ready: a paywall says so rather than offering to ask again.
const inProgressStates: ReadonlySet<EntitlementRequestState> = new Set([
  EntitlementRequestState.REQUESTED,
  EntitlementRequestState.GRANTED,
  EntitlementRequestState.PREPARING,
]);

function isForTarget({ target: asked }: CustomerAccessRequest, target: RequestTarget): boolean {
  if ('scenarioId' in target) {
    return asked.key === target.key && asked.companyId === target.companyId && asked.scenarioId === target.scenarioId;
  }
  return 'key' in target
    ? asked.key === target.key && asked.companyId === null
    : asked.companyId === target.companyId && !asked.scenarioId;
}

/**
 * The request a paywall shows for the target: the latest of the caller's requests for it that is still
 * on its way, or the one just created for it until the list has been read again. Once the list holds
 * the created request the list decides, so one withdrawn, declined or ended there offers the action
 * again.
 */
export function findShownRequest(
  listed: readonly CustomerAccessRequest[],
  justCreated: CustomerAccessRequest | undefined,
  target: RequestTarget,
): CustomerAccessRequest | undefined {
  const inProgress = listed
    .filter(request => inProgressStates.has(request.state) && isForTarget(request, target))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .at(0);
  if (inProgress || !justCreated || !isForTarget(justCreated, target)) {
    return inProgress;
  }
  return listed.some(({ requestId }) => requestId === justCreated.requestId) ? undefined : justCreated;
}

/** The request catalogue's key for a locked feature, or undefined when it cannot be requested. */
export function toRequestableKey(featureId: FeatureId): string | undefined {
  const key = productKeyByFeatureId[featureId];
  return key === undefined ? undefined : findRequestCatalogueEntry(key)?.key;
}

/**
 * The organisation a signed-in customer requests access for, while it holds `USE_SELF_SERVE_ACCESS`;
 * otherwise undefined, and the paywall keeps its "contact us". The team acts on requests through the
 * queue, so it never gets the customer's request action.
 */
export function useRequestAccessOrganizationId(): number | undefined {
  const user = useGetLoggedInUser();
  const isTeam = useIsTeam();
  const { hasPrivilege } = useHasResourcelessPrivilege(ResourcelessPermissionId.USE_SELF_SERVE_ACCESS);
  if (!user || isTeam || !hasPrivilege) {
    return undefined;
  }
  return getOrganizationOfUser(user)?.id;
}
